import React from 'react'
import { withFirebase } from '../../components/Fb'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import moment from 'moment'

import KpiCard from '../../components/Report/WeeklySummary/KpiCard'
import SalesSparklineCard from '../../components/Report/WeeklySummary/SalesSparklineCard'
import CategorySalesCard from '../../components/Report/WeeklySummary/CategorySalesCard'
import ProductTypeSalesCard from '../../components/Report/WeeklySummary/ProductTypeSalesCard'
import OccupancyByTypeCard from '../../components/Report/WeeklySummary/OccupancyByTypeCard'
import WeeklyBreakdownTable from '../../components/Report/WeeklySummary/WeeklyBreakdownTable'
import CategoryBreakdownTable from '../../components/Report/WeeklySummary/CategoryBreakdownTable'
import YtdCumulativeCard from '../../components/Report/WeeklySummary/YtdCumulativeCard'
import CustomerActivityCard from '../../components/Report/WeeklySummary/CustomerActivityCard'
import OccupancyHeatmapCard from '../../components/Report/WeeklySummary/OccupancyHeatmapCard'

const MONTH_NAMES_FI = [
  'Tammikuu', 'Helmikuu', 'Maaliskuu', 'Huhtikuu', 'Toukokuu', 'Kesäkuu',
  'Heinäkuu', 'Elokuu', 'Syyskuu', 'Lokakuu', 'Marraskuu', 'Joulukuu',
]
const MONTH_SHORT_FI = ['Tam', 'Hel', 'Maa', 'Huh', 'Tou', 'Kes', 'Hei', 'Elo', 'Syy', 'Lok', 'Mar', 'Jou']

const WEEKDAY_LABELS = ['Ma', 'Ti', 'Ke', 'To', 'Pe']
const WEEKEND_LABELS = ['La', 'Su']

const monthRange = (selectedDate) => {
  const m = moment.utc(selectedDate, 'YYYY-MM-DD')
  return {
    start: m.clone().startOf('month').format('YYYY-MM-DD'),
    end: m.clone().endOf('month').format('YYYY-MM-DD'),
  }
}

const previousMonthRange = (selectedDate) => {
  const m = moment.utc(selectedDate, 'YYYY-MM-DD').subtract(1, 'month')
  return {
    start: m.clone().startOf('month').format('YYYY-MM-DD'),
    end: m.clone().endOf('month').format('YYYY-MM-DD'),
  }
}

// N most recent calendar months ending at selectedDate's month, oldest first.
const lastNMonthsRanges = (selectedDate, n) => {
  const ranges = []
  for (let i = n - 1; i >= 0; i--) {
    const m = moment.utc(selectedDate, 'YYYY-MM-DD').subtract(i, 'month')
    ranges.push({
      start: m.clone().startOf('month').format('YYYY-MM-DD'),
      end: m.clone().endOf('month').format('YYYY-MM-DD'),
      label: MONTH_SHORT_FI[m.month()],
    })
  }
  return ranges
}

// Twelve calendar months of the year that contains the given selectedDate.
const yearMonthsRanges = (year) => {
  const ranges = []
  for (let i = 0; i < 12; i++) {
    const m = moment.utc(`${year}-01-01`).add(i, 'month')
    ranges.push({
      start: m.clone().startOf('month').format('YYYY-MM-DD'),
      end: m.clone().endOf('month').format('YYYY-MM-DD'),
      label: MONTH_SHORT_FI[i],
    })
  }
  return ranges
}

// ISO weeks that intersect the given month [start..end], oldest first.
const weeksInMonthRanges = (selectedDate) => {
  const m = moment.utc(selectedDate, 'YYYY-MM-DD')
  const monthStart = m.clone().startOf('month')
  const monthEnd = m.clone().endOf('month')
  const ranges = []
  const cursor = monthStart.clone().startOf('isoWeek')
  while (cursor.isSameOrBefore(monthEnd, 'day')) {
    const weekStart = cursor.clone()
    const weekEnd = cursor.clone().endOf('isoWeek')
    ranges.push({
      weekLabel: `Vk ${weekStart.isoWeek()}`,
      dateRange: `${weekStart.format('D.M.')} – ${weekEnd.format('D.M.')}`,
      start: weekStart.format('YYYY-MM-DD'),
      end: weekEnd.format('YYYY-MM-DD'),
    })
    cursor.add(1, 'week')
  }
  return ranges
}

const sumCost = (rows) => (rows || []).reduce((acc, r) => acc + (Number(r.cost) || 0), 0)

const rowWeekdayLabel = (row) => {
  if (row && row.day) return row.day
  if (row && row.dayIndex) {
    const idx = moment.utc(row.dayIndex, 'YYYY-MM-DD').isoWeekday() - 1
    return ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su'][idx]
  }
  return null
}

const occupancyPercent = (rows, allowedDays) => {
  if (!rows || rows.length === 0) return null
  let occ = 0
  let win = 0
  for (const r of rows) {
    const wd = rowWeekdayLabel(r)
    if (allowedDays && !allowedDays.includes(wd)) continue
    occ += Number(r.occupancy) || 0
    win += Number(r.windowLength) || 0
  }
  if (win === 0) return null
  return (occ / win) * 100
}

const aggregateByCategory = (rowsCurrent, rowsPrevious) => {
  const map = new Map()
  const accum = (rows, key) => {
    for (const r of rows || []) {
      const cat = r.category || '—'
      const cur = map.get(cat) || { category: cat, current: 0, previous: 0 }
      cur[key] += Number(r.cost) || 0
      map.set(cat, cur)
    }
  }
  accum(rowsCurrent, 'current')
  accum(rowsPrevious, 'previous')
  return Array.from(map.values()).sort((a, b) => (b.current + b.previous) - (a.current + a.previous))
}

const aggregateByProductType = (rowsCurrent, rowsPrevious) => {
  const all = [...(rowsCurrent || []), ...(rowsPrevious || [])]
  const hasField = all.some((r) => r && typeof r.producttype === 'string' && r.producttype.length > 0)
  if (!hasField) return []
  const map = new Map()
  const accum = (rows, key) => {
    for (const r of rows || []) {
      const t = (r && r.producttype) || 'other'
      const cur = map.get(t) || { type: t, current: 0, previous: 0 }
      cur[key] += Number(r.cost) || 0
      map.set(t, cur)
    }
  }
  accum(rowsCurrent, 'current')
  accum(rowsPrevious, 'previous')
  return Array.from(map.values()).sort((a, b) => (b.current + b.previous) - (a.current + a.previous))
}

const occupancyByType = (rows, allowedDays) => {
  if (!rows || rows.length === 0) return { irto: null, vakio: null }
  const hasField = rows.some((r) => r && (r.reservationtype === 'single' || r.reservationtype === 'recurring'))
  if (!hasField) return { irto: null, vakio: null }
  const acc = { single: { occ: 0, win: 0 }, recurring: { occ: 0, win: 0 } }
  for (const r of rows) {
    const wd = rowWeekdayLabel(r)
    if (allowedDays && !allowedDays.includes(wd)) continue
    const bucket = r.reservationtype === 'recurring' ? acc.recurring : acc.single
    bucket.occ += Number(r.occupancy) || 0
    bucket.win += Number(r.windowLength) || 0
  }
  return {
    irto: acc.single.win > 0 ? (acc.single.occ / acc.single.win) * 100 : null,
    vakio: acc.recurring.win > 0 ? (acc.recurring.occ / acc.recurring.win) * 100 : null,
  }
}

// Builds rows for the WeeklyBreakdownTable: one entry per ISO week intersecting
// the selected month. Uses busyrawRows + salesRows, both already scoped to the
// month range.
const aggregateByWeekInMonth = (busyrawRows, salesRows, selectedDate) => {
  const ranges = weeksInMonthRanges(selectedDate)
  const map = new Map()
  for (const r of ranges) {
    map.set(r.weekLabel, { ...r, sales: 0, occupancy: 0, windowLength: 0 })
  }
  const findBucket = (dateStr) => {
    const m = moment.utc(dateStr, 'YYYY-MM-DD')
    const wkLabel = `Vk ${m.isoWeek()}`
    return map.get(wkLabel)
  }
  for (const r of busyrawRows || []) {
    const bucket = r.dayIndex ? findBucket(r.dayIndex) : null
    if (!bucket) continue
    bucket.occupancy += Number(r.occupancy) || 0
    bucket.windowLength += Number(r.windowLength) || 0
  }
  for (const r of salesRows || []) {
    const dateKey = r.occurency ? moment.utc(r.occurency).format('YYYY-MM-DD') : null
    const bucket = dateKey ? findBucket(dateKey) : null
    if (!bucket) continue
    bucket.sales += Number(r.cost) || 0
  }
  return Array.from(map.values())
}

class MonthlySummaryContainer extends React.Component {
  state = {
    selectedDate: moment.utc().format('YYYY-MM-DD'),

    salesCurrent: null,
    salesPrevious: null,
    salesTrend: [],
    productTypeSales: [],
    currentMonthSalesRows: [],
    salesLoading: true,

    weekdayOccupancyCurrent: null,
    weekdayOccupancyPrevious: null,
    weekendOccupancyCurrent: null,
    weekendOccupancyPrevious: null,
    categorySales: [],
    weekdayByType: { irto: { current: null, previous: null }, vakio: { current: null, previous: null } },
    weekendByType: { irto: { current: null, previous: null }, vakio: { current: null, previous: null } },
    currentMonthBusyrawRows: [],
    occupancyLoading: true,

    ytdData: null,
    ytdLoading: true,

    customerActivity: { active: { current: null, previous: null }, new: { current: null, previous: null } },
    customerActivityLoading: true,

    heatmapData: { categories: [] },
    heatmapLoading: true,
  }

  componentDidMount() {
    this.fetchAll()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.selectedDate !== this.state.selectedDate) {
      this.fetchAll()
    }
  }

  moveDate = (months) => {
    const next = months === 0
      ? moment.utc().format('YYYY-MM-DD')
      : moment.utc(this.state.selectedDate).add(months, 'month').format('YYYY-MM-DD')
    this.setState({ selectedDate: next })
  }

  fetchAll = () => {
    this.fetchSales()
    this.fetchOccupancy()
    this.fetchYtd()
    this.fetchCustomerActivity()
    this.fetchHeatmap()
  }

  fetchSales = async () => {
    this.setState({ salesLoading: true })
    const ranges = lastNMonthsRanges(this.state.selectedDate, 6)
    try {
      const responses = await Promise.all(
        ranges.map((r) => this.props.firebase.getReport(r.start, r.end, 'sales', '00:00', '23:59'))
      )
      const rowsByMonth = responses.map((res) => (res && res.data && res.data.rows) || [])
      const totals = rowsByMonth.map((rows) => sumCost(rows))
      const salesTrend = ranges.map((r, i) => ({ label: r.label, total: totals[i] }))
      const curRows = rowsByMonth[rowsByMonth.length - 1]
      const prevRows = rowsByMonth[rowsByMonth.length - 2]
      this.setState({
        salesTrend,
        salesCurrent: totals[totals.length - 1],
        salesPrevious: totals[totals.length - 2],
        productTypeSales: aggregateByProductType(curRows, prevRows),
        currentMonthSalesRows: curRows,
        salesLoading: false,
      })
    } catch (e) {
      console.error('monthly summary sales fetch failed', e)
      this.setState({ salesTrend: [], salesCurrent: null, salesPrevious: null, productTypeSales: [], currentMonthSalesRows: [], salesLoading: false })
    }
  }

  fetchOccupancy = async () => {
    this.setState({ occupancyLoading: true })
    const cur = monthRange(this.state.selectedDate)
    const prev = previousMonthRange(this.state.selectedDate)
    try {
      const [curRes, prevRes] = await Promise.all([
        this.props.firebase.getReport(cur.start, cur.end, 'busyraw', '06:00', '23:00', true),
        this.props.firebase.getReport(prev.start, prev.end, 'busyraw', '06:00', '23:00', true),
      ])
      const curRows = (curRes && curRes.data && curRes.data.rows) || []
      const prevRows = (prevRes && prevRes.data && prevRes.data.rows) || []
      const wkCur = occupancyByType(curRows, WEEKDAY_LABELS)
      const wkPrev = occupancyByType(prevRows, WEEKDAY_LABELS)
      const weCur = occupancyByType(curRows, WEEKEND_LABELS)
      const wePrev = occupancyByType(prevRows, WEEKEND_LABELS)
      this.setState({
        weekdayOccupancyCurrent: occupancyPercent(curRows, WEEKDAY_LABELS),
        weekdayOccupancyPrevious: occupancyPercent(prevRows, WEEKDAY_LABELS),
        weekendOccupancyCurrent: occupancyPercent(curRows, WEEKEND_LABELS),
        weekendOccupancyPrevious: occupancyPercent(prevRows, WEEKEND_LABELS),
        categorySales: aggregateByCategory(curRows, prevRows),
        weekdayByType: {
          irto: { current: wkCur.irto, previous: wkPrev.irto },
          vakio: { current: wkCur.vakio, previous: wkPrev.vakio },
        },
        weekendByType: {
          irto: { current: weCur.irto, previous: wePrev.irto },
          vakio: { current: weCur.vakio, previous: wePrev.vakio },
        },
        currentMonthBusyrawRows: curRows,
        occupancyLoading: false,
      })
    } catch (e) {
      console.error('monthly summary occupancy fetch failed', e)
      this.setState({
        weekdayOccupancyCurrent: null,
        weekdayOccupancyPrevious: null,
        weekendOccupancyCurrent: null,
        weekendOccupancyPrevious: null,
        categorySales: [],
        weekdayByType: { irto: { current: null, previous: null }, vakio: { current: null, previous: null } },
        weekendByType: { irto: { current: null, previous: null }, vakio: { current: null, previous: null } },
        currentMonthBusyrawRows: [],
        occupancyLoading: false,
      })
    }
  }

  // YTD: cumulative sales from Jan 1 of selected year + same for previous year.
  // We fetch Jan..selectedMonth for the current year (avoids future-month calls)
  // and Jan..Dec for the previous year.
  fetchYtd = async () => {
    this.setState({ ytdLoading: true })
    const m = moment.utc(this.state.selectedDate, 'YYYY-MM-DD')
    const currentYear = m.year()
    const previousYear = currentYear - 1
    const monthIndex = m.month() // 0-based; we include this month
    const currentRanges = yearMonthsRanges(currentYear).slice(0, monthIndex + 1)
    const previousRanges = yearMonthsRanges(previousYear)
    try {
      const [currentResponses, previousResponses] = await Promise.all([
        Promise.all(currentRanges.map((r) => this.props.firebase.getReport(r.start, r.end, 'sales', '00:00', '23:59'))),
        Promise.all(previousRanges.map((r) => this.props.firebase.getReport(r.start, r.end, 'sales', '00:00', '23:59'))),
      ])
      const currentMonthly = currentResponses.map((res) => sumCost(res && res.data && res.data.rows))
      const previousMonthly = previousResponses.map((res) => sumCost(res && res.data && res.data.rows))
      let runCur = 0
      const currentCum = currentMonthly.map((v) => (runCur += v))
      let runPrev = 0
      const previousCum = previousMonthly.map((v) => (runPrev += v))
      this.setState({
        ytdData: {
          months: previousRanges.map((r) => r.label),
          current: currentCum,
          previous: previousCum,
          currentYearLabel: String(currentYear),
          previousYearLabel: String(previousYear),
        },
        ytdLoading: false,
      })
    } catch (e) {
      console.error('monthly summary ytd fetch failed', e)
      this.setState({ ytdData: null, ytdLoading: false })
    }
  }

  fetchCustomerActivity = async () => {
    this.setState({ customerActivityLoading: true })
    const cur = monthRange(this.state.selectedDate)
    const prev = previousMonthRange(this.state.selectedDate)
    try {
      const [curRes, prevRes] = await Promise.all([
        this.props.firebase.getCustomerActivityReport(cur.start, cur.end),
        this.props.firebase.getCustomerActivityReport(prev.start, prev.end),
      ])
      const curRow = curRes && curRes.data && curRes.data.rows && curRes.data.rows[0]
      const prevRow = prevRes && prevRes.data && prevRes.data.rows && prevRes.data.rows[0]
      if (!curRow || curRes.data.error) {
        this.setState({
          customerActivity: { active: { current: null, previous: null }, new: { current: null, previous: null } },
          customerActivityLoading: false,
        })
        return
      }
      this.setState({
        customerActivity: {
          active: { current: Number(curRow.activeCount) || 0, previous: prevRow ? Number(prevRow.activeCount) || 0 : 0 },
          new: { current: Number(curRow.newCount) || 0, previous: prevRow ? Number(prevRow.newCount) || 0 : 0 },
        },
        customerActivityLoading: false,
      })
    } catch (e) {
      console.error('monthly summary customer activity fetch failed', e)
      this.setState({
        customerActivity: { active: { current: null, previous: null }, new: { current: null, previous: null } },
        customerActivityLoading: false,
      })
    }
  }

  fetchHeatmap = async () => {
    this.setState({ heatmapLoading: true })
    const cur = monthRange(this.state.selectedDate)
    try {
      const res = await this.props.firebase.getHeatmapHourlyReport(cur.start, cur.end, '06:00', '23:00')
      const rows = (res && res.data && res.data.rows) || []
      if (rows.length === 0 || (res.data && res.data.error)) {
        this.setState({ heatmapData: { categories: [] }, heatmapLoading: false })
        return
      }
      // Build per-category 7×18 matrix (weekday × hour 06-23). occupancy %.
      const HOURS = Array.from({ length: 18 }, (_, i) => 6 + i)
      const WEEKDAYS = ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su']
      const byCategory = new Map()
      for (const r of rows) {
        const cat = r.category || '—'
        if (!byCategory.has(cat)) {
          byCategory.set(cat, {
            occ: WEEKDAYS.map(() => HOURS.map(() => 0)),
            win: WEEKDAYS.map(() => HOURS.map(() => 0)),
          })
        }
        const wd = rowWeekdayLabel(r)
        const wdIdx = WEEKDAYS.indexOf(wd)
        const hourIdx = HOURS.indexOf(Number(r.hour))
        if (wdIdx < 0 || hourIdx < 0) continue
        const acc = byCategory.get(cat)
        acc.occ[wdIdx][hourIdx] += Number(r.occupancy) || 0
        acc.win[wdIdx][hourIdx] += Number(r.windowLength) || 0
      }
      const categories = Array.from(byCategory.entries()).map(([category, acc]) => ({
        category,
        unit: '%',
        matrix: acc.occ.map((occRow, i) =>
          occRow.map((o, j) => (acc.win[i][j] > 0 ? (o / acc.win[i][j]) * 100 : 0))
        ),
      }))
      this.setState({ heatmapData: { categories }, heatmapLoading: false })
    } catch (e) {
      console.error('monthly summary heatmap fetch failed', e)
      this.setState({ heatmapData: { categories: [] }, heatmapLoading: false })
    }
  }

  renderMonthNavigation = () => {
    const m = moment.utc(this.state.selectedDate, 'YYYY-MM-DD')
    const label = `${MONTH_NAMES_FI[m.month()]} ${m.year()}`
    const arrowSx = { cursor: 'pointer', mx: 0.5 }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
        <KeyboardArrowLeftIcon onClick={() => this.moveDate(-1)} sx={arrowSx} titleAccess='Edellinen kuukausi' />
        <Box component='strong'>{label}</Box>
        <KeyboardArrowRightIcon onClick={() => this.moveDate(1)} sx={arrowSx} titleAccess='Seuraava kuukausi' />
        <Button size='small' variant='outlined' sx={{ ml: 1 }} onClick={() => this.moveDate(0)}>
          Tämä kuukausi
        </Button>
      </Box>
    )
  }

  render() {
    const {
      selectedDate,
      salesCurrent, salesPrevious, salesTrend, productTypeSales, currentMonthSalesRows, salesLoading,
      weekdayOccupancyCurrent, weekdayOccupancyPrevious,
      weekendOccupancyCurrent, weekendOccupancyPrevious,
      categorySales, weekdayByType, weekendByType,
      currentMonthBusyrawRows,
      occupancyLoading,
      ytdData, ytdLoading,
      customerActivity, customerActivityLoading,
      heatmapData, heatmapLoading,
    } = this.state
    const weeklyBreakdown = aggregateByWeekInMonth(currentMonthBusyrawRows, currentMonthSalesRows, selectedDate)
    const COMPARE = 'vs viime kuukausi'
    const CUR_LABEL = 'Tämä kuukausi'
    const PREV_LABEL = 'Viime kuukausi'

    return (
      <Box className='content'>
        <Box>
          <Typography variant='h5' sx={{ mb: 2 }}>Kuukausikooste</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>{this.renderMonthNavigation()}</Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <KpiCard
              title='Kuukauden myynti'
              value={salesCurrent}
              previous={salesPrevious}
              unit='€'
              loading={salesLoading}
              comparisonLabel={COMPARE}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <KpiCard
              title='Arki varausaste'
              value={weekdayOccupancyCurrent}
              previous={weekdayOccupancyPrevious}
              unit='%'
              loading={occupancyLoading}
              comparisonLabel={COMPARE}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <KpiCard
              title='Viikonlopun varausaste'
              value={weekendOccupancyCurrent}
              previous={weekendOccupancyPrevious}
              unit='%'
              loading={occupancyLoading}
              comparisonLabel={COMPARE}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <SalesSparklineCard title='Myynnin trendi (6 kk)' points={salesTrend} loading={salesLoading} />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <CustomerActivityCard data={customerActivity} loading={customerActivityLoading} />
          </Grid>
          <Grid size={{ xs: 12, lg: 9 }}>
            <YtdCumulativeCard data={ytdData} loading={ytdLoading} />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <CategorySalesCard
              data={categorySales}
              loading={occupancyLoading}
              currentLabel={CUR_LABEL}
              previousLabel={PREV_LABEL}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <ProductTypeSalesCard
              data={productTypeSales}
              loading={salesLoading}
              currentLabel={CUR_LABEL}
              previousLabel={PREV_LABEL}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <OccupancyByTypeCard
              title='Arki varausaste irto / vakio'
              data={weekdayByType}
              loading={occupancyLoading}
              currentLabel={CUR_LABEL}
              previousLabel={PREV_LABEL}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <OccupancyByTypeCard
              title='Viikonlopun varausaste irto / vakio'
              data={weekendByType}
              loading={occupancyLoading}
              currentLabel={CUR_LABEL}
              previousLabel={PREV_LABEL}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <OccupancyHeatmapCard data={heatmapData} loading={heatmapLoading} />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <WeeklyBreakdownTable rows={weeklyBreakdown} loading={salesLoading || occupancyLoading} />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <CategoryBreakdownTable
              data={categorySales}
              loading={occupancyLoading}
              currentLabel={CUR_LABEL}
              previousLabel={PREV_LABEL}
            />
          </Grid>
        </Grid>
      </Box>
    )
  }
}

export default withFirebase(MonthlySummaryContainer)
